import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ConnectedProps } from "react-redux";
import { isMobile, isTablet } from "react-device-detect";
import { useLocation } from "react-router";
import Loading from "../../components/Loading";
import { ClavaContext, ClavaRootContext, DropDownContext } from "../../../config/contexts";
import { DropDownContextType, DropdownEntry } from "../../../config/types";
import MobilePopup from "../../components/MobilePopup";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { isPremium } from "../../../config/utils";
import { connector } from "./redux";
import MainRoutes from "./MainRoutes";
import DropdownLayer from "../../components/DropdownLayer";
import { useAppBootstrap } from "../../../hooks/useAppBootstrap";
import { useUserStatusEffect } from "../../../hooks/useUserStatusEffect";
import { useUserBootstrap } from "../../../hooks/useUserBootstrap";
import { initAmplitude, trackAmplitude } from "../../../config/amplitude";

const routes = ["match", "imprint", "home", "pw-forgot", "league", "login", "profile", "register", "team"];
const subRoutes = ["management", "playerstats", "totw", "table", "teamstats", "matches", "highlights", "lineup", "squad", "statistics"];

const Main: React.FC<ConnectedProps<typeof connector>> = ({
                                                            user,
                                                            refreshToken,
                                                            createUser,
                                                            languageObject,
                                                            aoi,
                                                            error,
                                                            initBaseDataUser,
                                                            resetStoredData,
                                                            status,
                                                            getLeagues,
                                                            sports
                                                          }) => {
  const { fbToken, theme } = useContext(ClavaRootContext);
  const [dropDowns, setDropDowns] = useState<DropdownEntry[]>([]);

  useAppBootstrap(initBaseDataUser);

  useUserStatusEffect({
    status,
    error,
    aoi,
    languageObject,
    user,
    fbToken,
    createUser,
    refreshToken
  });

  useUserBootstrap({ user, sports, getLeagues, resetStoredData });

  const closeDropdown = useCallback((dropdownId?: string) => {
    setDropDowns((prev) => {
      if (!dropdownId) return [];
      return prev.filter((entry) => entry.id !== dropdownId);
    });
  }, []);

  const openDropdown = useCallback((entry: DropdownEntry) => {
    setDropDowns((prev) => {
      const without = prev.filter((item) => item.id !== entry.id);
      return [...without, entry];
    });
  }, []);

  const dropDownContextVal = useMemo<DropDownContextType>(
    () => ({
      setClose: closeDropdown,
      setOpen: openDropdown
    }),
    [closeDropdown, openDropdown]
  );
  useEffect(() => {
    if (user) {
      initAmplitude(user);
    }
  }, [user]);
  const clavaContextValue = useMemo(
    () =>
      user
        ? {
          l: user.language.locale,
          aoi: user.areaOfInterest.id,
          user,
          pro: isPremium(user)
        }
        : null,
    [user]
  );
  const location = useLocation();
  useEffect(() => {
    const { pathname } = location;
    let screenName = "Home";
    let screenClass = "";
    if (pathname !== "/") {
      const parts = pathname.split("/");
      parts.forEach((part) => {
        if (routes.includes(part)) {
          screenName = part.slice(0, 1).toUpperCase() + part.slice(1);
        }
        if (subRoutes.includes(part)) {
          screenClass = part.slice(0, 1).toUpperCase() + part.slice(1);
        }
      });
    }
    trackAmplitude("screen_view", { screen_name: screenName, screen_class: screenClass });

  }, [location]);
  if (!clavaContextValue) return <Loading />;

  return (
    <ClavaContext.Provider value={clavaContextValue}>
      <DropDownContext.Provider value={dropDownContextVal}>
        {isMobile && !isTablet ? (
          <>
            <MobilePopup />
            <Footer />
          </>
        ) : (
          <div
            className={`relative w-full min-h-[100vh] ${theme} bg-bg-50 dark:bg-bg-50-dark font-gilroy font-normal text-font dark:text-font-dark overflow-x-hidden flex flex-col`}
          >
            <Header />
            <div className="flex-1 flex flex-col pt-32">
              <MainRoutes user={clavaContextValue.user} />
              <DropdownLayer entries={dropDowns} onClose={closeDropdown} />
            </div>
            <Footer />
          </div>
        )}
      </DropDownContext.Provider>
    </ClavaContext.Provider>
  );
};

export default connector(Main);
